import React, { memo, useEffect, useState } from 'react';

const scriptPromises = {};

/**
 * Injects a `<script>` for `url` once per page and resolves when it has loaded.
 *
 * A failed load is forgotten so the next caller can retry it.
 *
 * @param {string} url - script source
 * @returns {Promise<void>} settles when the script loads or fails
 */
const loadScript = (url) => {
    if (!scriptPromises[url]) {
        scriptPromises[url] = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = url;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => {
                delete scriptPromises[url];
                script.remove();
                reject(new Error(`Failed to load script ${url}`));
            };
            document.head.appendChild(script);
        });
    }

    return scriptPromises[url];
};

/**
 * Replacement for react-google-maps' `withScriptjs`, whose recompose dependency
 * calls `React.createFactory` (removed in React 19).
 *
 * Renders `loadingElement` until the script at `googleMapURL` has loaded, then
 * renders `Component` with the remaining props. Nothing loads during server
 * rendering, so the server output is always `loadingElement`.
 *
 * @param {React.ComponentType} Component - component that needs the script
 * @returns {React.ComponentType} component taking `googleMapURL` and `loadingElement`
 */
const withScriptjs = (Component) => {
    const WithScriptjs = ({ googleMapURL, loadingElement, ...props }) => {
        const [loaded, setLoaded] = useState(false);

        useEffect(() => {
            let active = true;

            loadScript(googleMapURL)
                .then(() => active && setLoaded(true))
                .catch((error) => console.error(error));

            return () => {
                active = false;
            };
        }, [googleMapURL]);

        return loaded ? <Component {...props} /> : loadingElement;
    };

    WithScriptjs.displayName = `withScriptjs(${Component.displayName || Component.name || 'Component'})`;

    return memo(WithScriptjs);
};

export default withScriptjs;
